import { useEffect, useRef, useState } from 'react'
import { ObservableQueryFields } from 'react-apollo'
import { NetworkStatus } from 'apollo-client'
import isEqual from 'lodash.isequal'

import { handleFetchMore } from 'components/InfiniteList'
import { useLatestShareVersionSubscribe } from '../ShareVersionContext'

import { useGetVersionHistoryQuery } from '@sketch/gql-types'

const ENTRIES_PATH = ['share', 'versionHistory', 'entries']
const AFTER_PATH = ['share', 'versionHistory', 'meta', 'after']

const createHandleFetchLatest = (
  fetchMore: ObservableQueryFields<any, any>['fetchMore']
) =>
  handleFetchMore(fetchMore, ENTRIES_PATH, {
    fetchLatest: true,
    preserveAfter: true,
    afterPath: AFTER_PATH,
  })

const useGetShareVersions = (shareIdentifier: string) => {
  const query = useGetVersionHistoryQuery({
    variables: {
      shareIdentifier,
    },
    shouldInvalidatePrevious: (...args) => !isEqual(...args),
  })

  const [initialNetworkStatus] = useState(query.networkStatus)

  /* Make sure that the loadLatestHandler is always updated, but is only called once */
  const loadLatestHandler = useRef(createHandleFetchLatest(query.fetchMore))

  useEffect(() => {
    loadLatestHandler.current = createHandleFetchLatest(query.fetchMore)
  }, [query.fetchMore])

  useEffect(() => {
    /* Call the "loadLatestHandler" when mounted to refresh the list */
    /* But only when the request is already cached initially */
    initialNetworkStatus === NetworkStatus.ready && loadLatestHandler.current()
  }, [initialNetworkStatus])

  useLatestShareVersionSubscribe({
    shareID: shareIdentifier,
    onVersionUpdate: () => {
      loadLatestHandler.current()
    },
  })

  return query
}

export default useGetShareVersions
